import React, { memo, useEffect, useCallback } from 'react';

import isEqual from 'lodash/isEqual';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { PerformanceMark } from '@atlassian/jira-common-performance';

import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import type { IssueViewRelayFragment } from '@atlassian/jira-issue-fetch-services-common';
import { useIssueFieldConfig } from '@atlassian/jira-issue-field-base';
import { usePinnedFields } from '@atlassian/jira-issue-field-pin/src/utils';
import { CONTEXT_AREA } from '@atlassian/jira-issue-layout-common-constants';
import { PINNED_FIELDS_SORTABLE_ID } from '@atlassian/jira-issue-view-common-constants/src/layout';
import {
	PINNED_FIELDS_PANEL,
	PIN_FIELD_PLACEHOLDER_BANNER,
	DETAILS_PANEL,
} from '@atlassian/jira-issue-view-common-constants/src/onboarding-constants';
import { getLayoutItemId } from '@atlassian/jira-issue-view-layout';
import { FieldsCompactModeProvider } from '@atlassian/jira-issue-view-layout-templates-compact-mode-provider';
import * as PerfMarks from '@atlassian/jira-issue-view-layout-templates-constants/src/performance';
import { ItemList } from '@atlassian/jira-issue-view-layout-templates-item-list';
import {
	PinnedFieldHighlightWrapper,
	pinnedFieldHighlightWrapperSelectorName,
	PinnedFieldsBanner,
} from '@atlassian/jira-issue-view-layout-templates-pinned-fields-banner';
import type { LayoutItems } from '@atlassian/jira-issue-view-layout-templates-services';
import { ContextGroup } from '@atlassian/jira-issue-view-layout-templates-views-context-group';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectKey, useApplication, useEdition } from '@atlassian/jira-project-context-service';
import { useGlobalRefStoreActions } from '@atlassian/jira-refs-store';
import type { Application, ApplicationEdition } from '@atlassian/jira-shared-types';
import { useGenerateAdditionalAnalyticsEventAttributes } from '../../../../common/utils';
import { isSortingPinnedFieldsEnabled } from '../../../../feature-flags';
import { generateContextPanels } from './context-group';
import messages from './messages';

const SHOW_MORE = 'SHOW_MORE' as const;

type Props = {
	// TODO Decomp BENTO-12514 - add useFragment to this component and replace this prop with more specific fragment key
	issueViewRelayFragment?: IssueViewRelayFragment | null;
	items: LayoutItems;
	onToggleExpanded?: (value: boolean) => void;
};

type DivRefWrapperProps = {
	children: React.ReactNode;
	registerKey: string;
};

const DivWithRef = ({ children, registerKey }: DivRefWrapperProps) => {
	const [, { register }] = useGlobalRefStoreActions();

	const onRef = useCallback(
		(ref: HTMLElement | null) => {
			register(registerKey, ref);
		},
		[register, registerKey],
	);

	return <div ref={onRef}>{children}</div>;
};

export const VisibleHiddenContextSectionView = ({
	items,
	onToggleExpanded,
	issueViewRelayFragment,
}: Props) => {
	const { formatMessage } = useIntl();
	const issueKey = useIssueKey();
	const [{ value: issueFieldsConfig }] = useIssueFieldConfig(issueKey);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, , sortPinnedFields] = isSortingPinnedFieldsEnabled() ? usePinnedFields(issueKey) : []; // eslint-disable-line react-hooks/rules-of-hooks -- Remove ternary when cleaning isSortingPinnedFieldsEnabled https://hello.atlassian.net/browse/DEE-914

	let application: Application | undefined;
	let edition: ApplicationEdition | undefined;
	if (ff('instrumentation-of-issue-ecosystem-reliability-slo-metrics-rhs_tpmqi')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks -- safe to disable as it's only behind an FF check!
		const projectKey = useProjectKey(issueKey);
		// eslint-disable-next-line react-hooks/rules-of-hooks -- safe to disable as it's only behind an FF check!
		application = useApplication(projectKey, true);
		// eslint-disable-next-line react-hooks/rules-of-hooks -- safe to disable as it's only behind an FF check!
		edition = useEdition(projectKey, true);
	}
	useEffect(() => {
		fireTrackAnalytics(
			createAnalyticsEvent({
				action: 'success',
			}),
			'onboarding initialized',
			'bentoCollapsibleGroup',
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const getSubtitle = (accumulator: any, item: any) => {
		const id = getLayoutItemId(item);
		if (!issueFieldsConfig || !issueFieldsConfig[id]) {
			return accumulator;
		}
		if (accumulator.length === 0) {
			return `${issueFieldsConfig[id].title}`;
		}
		return `${accumulator}, ${issueFieldsConfig[id].title}`;
	};

	const additionalPinnedEventAttributes = useGenerateAdditionalAnalyticsEventAttributes(
		items.pinnedFields,
	);
	const additionalContextEventAttributes = useGenerateAdditionalAnalyticsEventAttributes(
		items.visibleItems,
	);
	const additionalContextHiddenAttributes = useGenerateAdditionalAnalyticsEventAttributes(
		items.hiddenItems,
	);

	const hiddenItemsSubtitle = items.hiddenItems.reduce(getSubtitle, '');
	const visibleItemsSubtitle = items.visibleItems.reduce(getSubtitle, '');
	const pinnedItemsSubtitle = items.pinnedFields.reduce(getSubtitle, '');
	const hasPinnedFields = items.pinnedFields.length > 0;

	return (
		<div data-testid="issue-view-layout-templates-views.ui.context.visible-hidden.ui.context-items">
			{!hasPinnedFields && (
				<DivWithRef registerKey={PIN_FIELD_PLACEHOLDER_BANNER}>
					<SpotlightTarget name={PIN_FIELD_PLACEHOLDER_BANNER}>
						<PinnedFieldsBanner />
					</SpotlightTarget>
				</DivWithRef>
			)}

			<PinnedFieldHighlightWrapper
				data-component-selector={pinnedFieldHighlightWrapperSelectorName}
			>
				<div data-testid="issue-view-layout-templates-views.ui.context.visible-hidden.ui.primary-items">
					{hasPinnedFields && (
						<DivWithRef registerKey={PINNED_FIELDS_PANEL}>
							<SpotlightTarget name={PINNED_FIELDS_PANEL}>
								<PerformanceMark metricKey={PerfMarks.PINNED_FIELDS_BEGIN_MARK_KEY} />
								<ContextGroup
									title={
										isSortingPinnedFieldsEnabled()
											? formatMessage(messages.pinnedFieldsTitle)
											: formatMessage(messages.pinnedFieldsText)
									}
									groupId="pinned-context-items"
									subTitle={pinnedItemsSubtitle}
									initialOpened
									additionalEventAttributes={additionalPinnedEventAttributes}
								>
									<ItemList
										items={items.pinnedFields}
										area={CONTEXT_AREA}
										layoutItemsDataFragment={issueViewRelayFragment ?? null}
										sortableId={
											isSortingPinnedFieldsEnabled() ? PINNED_FIELDS_SORTABLE_ID : undefined
										}
										onSortItems={isSortingPinnedFieldsEnabled() ? sortPinnedFields : undefined}
									/>
								</ContextGroup>
								<PerformanceMark metricKey={PerfMarks.PINNED_FIELDS_END_MARK_KEY} />
							</SpotlightTarget>
						</DivWithRef>
					)}
					{items.visibleItems.length > 0 && (
						<DivWithRef registerKey={DETAILS_PANEL}>
							<PerformanceMark metricKey={PerfMarks.DETAILS_FIELDS_BEGIN_MARK_KEY} />

							<ContextGroup
								title={formatMessage(messages.detailsText)}
								groupId="details-group"
								subTitle={visibleItemsSubtitle}
								initialOpened
								additionalEventAttributes={additionalContextEventAttributes}
							>
								<ItemList
									items={items.visibleItems}
									area={CONTEXT_AREA}
									layoutItemsDataFragment={issueViewRelayFragment ?? null}
								/>
							</ContextGroup>

							<PerformanceMark metricKey={PerfMarks.DETAILS_FIELDS_END_MARK_KEY} />
						</DivWithRef>
					)}
				</div>
				{items.hiddenItems.length > 0 && (
					<>
						<PerformanceMark metricKey={PerfMarks.MORE_FIELDS_BEGIN_MARK_KEY} />

						<SpotlightTarget name={SHOW_MORE}>
							<ContextGroup
								title={formatMessage(messages.moreFieldsText)}
								groupId="secondary-context-items"
								onChange={onToggleExpanded}
								subTitle={hiddenItemsSubtitle}
								initialOpened={false}
								additionalEventAttributes={additionalContextHiddenAttributes}
							>
								<ItemList
									items={items.hiddenItems}
									area={CONTEXT_AREA}
									layoutItemsDataFragment={issueViewRelayFragment ?? null}
								/>
							</ContextGroup>
						</SpotlightTarget>

						<PerformanceMark metricKey={PerfMarks.MORE_FIELDS_END_MARK_KEY} />
					</>
				)}
				{items.apps.contextPanels.length > 0 && (
					<>
						<PerformanceMark metricKey={PerfMarks.ECOSYSTEM_CONTEXT_BEGIN_MARK_KEY} />
						{ff('instrumentation-of-issue-ecosystem-reliability-slo-metrics-rhs_tpmqi')
							? generateContextPanels(
									items.apps.contextPanels,
									issueViewRelayFragment ?? null,
									application,
									edition,
								)
							: generateContextPanels(items.apps.contextPanels, issueViewRelayFragment ?? null)}
						<PerformanceMark metricKey={PerfMarks.ECOSYSTEM_CONTEXT_END_MARK_KEY} />
					</>
				)}
			</PinnedFieldHighlightWrapper>
		</div>
	);
};

const VisibleHiddenContextSectionViewInternal = (props: Props) => (
	<FieldsCompactModeProvider>
		<VisibleHiddenContextSectionView {...props} />
	</FieldsCompactModeProvider>
);

const MemoisedVisibleHiddenContextSectionView = memo<Props>(
	VisibleHiddenContextSectionViewInternal,
	isEqual,
);
MemoisedVisibleHiddenContextSectionView.displayName = 'MemoisedVisibleHiddenContextSectionView';

export default MemoisedVisibleHiddenContextSectionView;
